import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import PayrollCompensation from '@/components/payroll/PayrollCompensation';
import PayrollPayRuns from '@/components/payroll/PayrollPayRuns';
import PayrollWorkOrderQueue from '@/components/payroll/PayrollWorkOrderQueue';
import PayrollOverview from '@/components/payroll/PayrollOverview';
import PayrollImport from '@/components/payroll/PayrollImport';
import PayrollHistory from '@/components/payroll/PayrollHistory';
import SidebarShell from '@/components/wom/SidebarShell';
import { useSessionState } from '@/lib/sessionState';
import { moduleIcon, moduleColor } from '@/lib/moduleIdentity';
import {
  CalendarRange, Users2, FileCheck2, ClipboardList,
  ShieldCheck, Clock, Lock, Briefcase, LayoutDashboard, UploadCloud, History, FileBarChart2,
} from 'lucide-react';

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard, need: 'can_view' },
  { key: 'runs', label: 'Payroll Runs', icon: CalendarRange, need: 'can_view' },
  { key: 'compensation', label: 'Compensation', icon: Users2, need: 'can_prepare' },
  { key: 'import', label: 'Import History', icon: UploadCloud, need: 'can_import' },
  { key: 'history', label: 'Payroll History', icon: History, need: 'can_view' },
  { key: 'wo_queue', label: 'Work-Order Pay', icon: Briefcase, need: 'can_prepare' },
  { key: 'approvals', label: 'Approvals', icon: FileCheck2, need: 'can_approve' },
  { key: 'quickbooks', label: 'QuickBooks Entry', icon: ClipboardList, need: 'can_process' },
  { key: 'reports', label: 'Reports', icon: FileBarChart2, need: 'can_view' },
];

const PayrollBrand = () => (
  <div className="flex items-center justify-center border-b border-white/10 bg-white px-4 py-4" data-testid="payroll-brand">
    <img src={moduleIcon('payroll')} alt="Total Pay" className="w-[70%] max-w-[140px] h-auto object-contain" style={{ aspectRatio: '1 / 1' }} data-testid="payroll-brand-icon" />
  </div>
);

const PayrollHub = () => {
  const { token } = useContext(AuthContext);
  const [meta, setMeta] = useState(null);
  const [active, setActive] = useSessionState('payroll_active', 'overview');
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/payroll/meta`, auth(token))
      .then((r) => setMeta(r.data))
      .catch(() => setErr('Unable to load Payroll access.'));
  }, [token]);

  const nav = NAV.filter((n) => n.need === 'can_view' || (meta && meta[n.need]));

  const scopeNote = meta?.scope_limited ? (
    <div className="rounded-lg border border-white/15 bg-white/5 p-3 text-[11px] text-slate-200" data-testid="payroll-scope-note">
      <div className="flex items-center gap-1.5 font-semibold"><Lock size={12} /> Assigned access</div>
      You can enter payroll only for your own active assignments.
    </div>
  ) : null;

  return (
    <div data-testid="payroll-hub">
      <SidebarShell
        navItems={nav}
        activeKey={active}
        onNavigate={setActive}
        brandNode={<PayrollBrand />}
        mobileBrand={<img src={moduleIcon('payroll')} alt="Total Pay" className="h-8 w-8 object-contain" data-testid="payroll-mobile-brand" />}
        workspaceLabel="Total Pay"
        logoutTo="/login"
        bottomSlot={scopeNote}
        mobileNavGrid
        mobileNavAccent={moduleColor('payroll')}
        navAccent={moduleColor('payroll')}
      >
        {err && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600" data-testid="payroll-error">{err}</div>}
        {!err && active === 'overview' && <PayrollOverview meta={meta} onOpen={setActive} />}
        {!err && active === 'compensation' && <PayrollCompensation meta={meta} />}
        {!err && active === 'runs' && <PayrollPayRuns meta={meta} />}
        {!err && active === 'wo_queue' && <PayrollWorkOrderQueue meta={meta} />}
        {!err && active === 'import' && <PayrollImport meta={meta} />}
        {!err && active === 'history' && <PayrollHistory meta={meta} />}
        {!err && active === 'reports' && <PayrollHistory meta={meta} initialTab="reports" />}
        {!err && !['overview', 'compensation', 'runs', 'wo_queue', 'import', 'history', 'reports'].includes(active) && (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm" data-testid={`payroll-section-${active}`}>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E08A3C]/15 text-[#E08A3C]">
              <Clock size={26} />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-[#0E2A47]">{NAV.find((n) => n.key === active)?.label}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">
              This Payroll workflow is being built. Compensation setup, draft pay-run
              generation, gross-pay entry, budget comparison and owner approval arrive
              in the upcoming phases.
            </p>
            {meta && (
              <div className="mx-auto mt-6 flex max-w-md flex-wrap items-center justify-center gap-2 text-[11px]">
                {meta.is_global_admin && <span className="inline-flex items-center gap-1 rounded-full bg-[#8FC93A]/15 px-2.5 py-1 font-semibold text-[#4d7c0f]"><ShieldCheck size={12} /> Global Administrator</span>}
                {meta.can_prepare && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">Prepare</span>}
                {meta.can_approve && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">Approve</span>}
                {meta.can_process && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">QuickBooks Entry</span>}
                {meta.can_enter_assigned && !meta.can_prepare && <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">Enter (Assigned)</span>}
              </div>
            )}
          </div>
        )}
      </SidebarShell>
    </div>
  );
};

export default PayrollHub;
